import { makeAutoObservable, runInAction } from 'mobx';
import { userConfigApi } from '@/services/userConfigApi';
import type {
  ConfigDefinition,
  ConfigDefinitionItem,
  CreateConfigDefinitionRequest,
  UpdateConfigDefinitionRequest,
  UserConfigAssignment,
  UserConfigAssignmentItem,
} from '@/types';

function transformDefinition(d: ConfigDefinition): ConfigDefinitionItem {
  return {
    id: d.id,
    configKey: d.config_key,
    jsonValue: d.json_value,
    description: d.description,
    createAt: d.create_at,
    updateAt: d.update_at,
  };
}

function transformAssignment(
  a: UserConfigAssignment,
): UserConfigAssignmentItem {
  return {
    id: a.id,
    userId: a.user_id,
    configKey: a.config_key,
    createAt: a.create_at,
  };
}

class UserConfigStore {
  // --- Config definitions ---
  definitions: ConfigDefinitionItem[] = [];
  definitionsPagination = { page: 1, pageSize: 10, total: 0 };
  definitionsKeyword = '';
  definitionsLoading = false;

  // --- User assignment drawer ---
  activeConfigKey: string | null = null;
  assignedUsers: UserConfigAssignmentItem[] = [];
  usersPagination = { page: 1, pageSize: 10, total: 0 };
  usersLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // --- Definition actions ---

  async fetchDefinitions() {
    this.definitionsLoading = true;
    try {
      const response = await userConfigApi.listConfigDefinitions({
        page_num: this.definitionsPagination.page,
        page_size: this.definitionsPagination.pageSize,
        keyword: this.definitionsKeyword || undefined,
      });
      runInAction(() => {
        this.definitions = response.definitions.map(transformDefinition);
        this.definitionsPagination.total = response.total;
        this.definitionsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.definitionsLoading = false;
      });
      throw error;
    }
  }

  async createDefinition(data: CreateConfigDefinitionRequest) {
    this.definitionsLoading = true;
    try {
      await userConfigApi.createConfigDefinition(data);
      await this.fetchDefinitions();
    } catch (error) {
      runInAction(() => {
        this.definitionsLoading = false;
      });
      throw error;
    }
  }

  async updateDefinition(data: UpdateConfigDefinitionRequest) {
    this.definitionsLoading = true;
    try {
      await userConfigApi.updateConfigDefinition(data);
      await this.fetchDefinitions();
    } catch (error) {
      runInAction(() => {
        this.definitionsLoading = false;
      });
      throw error;
    }
  }

  async deleteDefinition(configKey: string) {
    this.definitionsLoading = true;
    try {
      await userConfigApi.deleteConfigDefinition({ config_key: configKey });
      await this.fetchDefinitions();
    } catch (error) {
      runInAction(() => {
        this.definitionsLoading = false;
      });
      throw error;
    }
  }

  setDefinitionsKeyword(keyword: string) {
    this.definitionsKeyword = keyword;
    this.definitionsPagination.page = 1;
  }

  setDefinitionsPagination(page: number, pageSize?: number) {
    this.definitionsPagination.page = page;
    if (pageSize) this.definitionsPagination.pageSize = pageSize;
  }

  // --- User assignment actions ---

  openUserDrawer(configKey: string) {
    this.activeConfigKey = configKey;
    this.assignedUsers = [];
    this.usersPagination = { page: 1, pageSize: 10, total: 0 };
    this.fetchAssignedUsers();
  }

  closeUserDrawer() {
    this.activeConfigKey = null;
    this.assignedUsers = [];
  }

  async fetchAssignedUsers() {
    if (!this.activeConfigKey) return;
    this.usersLoading = true;
    try {
      const response = await userConfigApi.listUsersByConfigKey({
        config_key: this.activeConfigKey,
        page_num: this.usersPagination.page,
        page_size: this.usersPagination.pageSize,
      });
      runInAction(() => {
        this.assignedUsers = response.items.map(transformAssignment);
        this.usersPagination.total = response.total;
        this.usersLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.usersLoading = false;
      });
      throw error;
    }
  }

  async assignUser(userId: string) {
    if (!this.activeConfigKey) return;
    this.usersLoading = true;
    try {
      await userConfigApi.addUserConfig({
        user_id: userId,
        config_key: this.activeConfigKey,
      });
      await this.fetchAssignedUsers();
    } catch (error) {
      runInAction(() => {
        this.usersLoading = false;
      });
      throw error;
    }
  }

  async batchAssignUsers(
    userIds: string[],
  ): Promise<{ added: number; skipped: number }> {
    if (!this.activeConfigKey) return { added: 0, skipped: 0 };
    this.usersLoading = true;
    try {
      const response = await userConfigApi.batchAddUserConfig({
        config_key: this.activeConfigKey,
        user_ids: userIds,
      });
      await this.fetchAssignedUsers();
      return { added: response.added, skipped: response.skipped };
    } catch (error) {
      runInAction(() => {
        this.usersLoading = false;
      });
      throw error;
    }
  }

  async removeUser(userId: string) {
    if (!this.activeConfigKey) return;
    this.usersLoading = true;
    try {
      await userConfigApi.removeUserConfig({
        user_id: userId,
        config_key: this.activeConfigKey,
      });
      await this.fetchAssignedUsers();
    } catch (error) {
      runInAction(() => {
        this.usersLoading = false;
      });
      throw error;
    }
  }

  setUsersPagination(page: number, pageSize?: number) {
    this.usersPagination.page = page;
    if (pageSize) this.usersPagination.pageSize = pageSize;
  }
}

export const userConfigStore = new UserConfigStore();
